import {
  IEdge,
  Rect,
  GraphComponent,
  ILabel,
  INode,
  IRenderContext,
  Font,
} from '@ardoq/yfiles';
import { type CollapsibleGraphGroup, type YGraphState } from '@ardoq/graph';
import {
  createMeasurerFromFont,
  MODERNIZED_BLOCK_DIAGRAM_LINE_HEIGHT as LINE_HEIGHT,
} from '@ardoq/graph-labeling';
import { wrapString } from 'tabview/blocks/misc/string';
import { MODERNIZED_BLOCK_DIAGRAM_OTHER_LABEL_FONT } from 'yfilesExtensions/styles/consts';
import {
  MODERNIZED_BLOCK_DIAGRAM_OTHER_LABEL_VERTICAL_PADDING as VERTICAL_PADDING,
  MODERNIZED_BLOCK_DIAGRAM_OTHER_LABEL_PADDING as PADDING,
  MODERNIZED_BLOCK_DIAGRAM_OTHER_LABELS_VERTICAL_MARGIN as VERTICAL_MARGIN,
  MODERNIZED_BLOCK_DIAGRAM_LEAF_NODE_PADDING,
} from 'yfilesExtensions/styles/modernized/consts';
import { GROUP_PADDING } from '../../consts';
import { MODERNIZED_BLOCK_DIAGRAM_LEAF_NODE_LABEL_WIDTH } from '../modernized/consts';

export const getNodeFromLabel = (label: ILabel): INode | null => {
  return label.owner instanceof INode ? (label.owner as INode) : null;
};

export const getGroupHeaders = (
  context: IRenderContext
): YGraphState['groupHeaders'] | null => {
  const graph = (context.canvasComponent as GraphComponent)?.graph;
  if (!graph) return null;

  const { groupHeaders } = graph.tag as YGraphState;
  return groupHeaders ?? null;
};

export const getGraph = (
  context: IRenderContext
): GraphComponent['graph'] | null => {
  return (context.canvasComponent as GraphComponent)?.graph ?? null;
};

export const getGraphTag = (context: IRenderContext): YGraphState | null => {
  const graph = getGraph(context);
  return (graph?.tag as YGraphState) ?? null;
};

export const getGraphGroupFromNode = (
  node: INode
): CollapsibleGraphGroup | null => {
  return (node.tag as CollapsibleGraphGroup | null) ?? null;
};

export const getLeafNodeLayout = (
  context: IRenderContext
): Map<string, any> | null => {
  try {
    const graphState = (context.canvasComponent as GraphComponent).graph
      .tag as YGraphState;
    return graphState?.leafNodeLayout || null;
  } catch {
    return null;
  }
};

export const getEdgeFromLabel = (label: ILabel): IEdge | null => {
  return label.owner instanceof IEdge ? (label.owner as IEdge) : null;
};

export const getEdgeLabels = (
  context: IRenderContext
): Map<string, string[]> | null => {
  try {
    const graphState = (context.canvasComponent as GraphComponent).graph
      .tag as YGraphState;
    return graphState?.edgeLabels || null;
  } catch {
    return null;
  }
};

export const calculateTextBounds = (
  wrappedText: string[],
  x: number,
  y: number,
  width: number,
  verticalPadding = 0
): Rect => {
  const height = wrappedText.length * LINE_HEIGHT + 2 * verticalPadding;
  return new Rect(x, y, width, height);
};

export const calculateStackedBounds = (
  textArrays: string[][],
  startX: number,
  startY: number,
  width: number,
  spacing: number,
  verticalPadding = 0
): Rect[] => {
  const bounds: Rect[] = [];
  let currentY = startY;

  for (const wrappedText of textArrays) {
    const height = wrappedText.length * LINE_HEIGHT + 2 * verticalPadding;
    bounds.push(new Rect(startX, currentY, width, height));
    currentY += height + spacing;
  }

  return bounds;
};

export const wrapTextArray = (
  texts: string[],
  maxWidth: number,
  measureFunction: (text: string) => number,
  maxLines: number
): string[][] => {
  return texts.map(text =>
    wrapString(text, maxWidth, measureFunction, maxLines)
  );
};

export type OtherLabelsLayoutConfig = {
  startX?: number;
  startY?: number;
  fixedWidth?: number | null;
  labelSpacing?: number;
  horizontalPadding?: number;
  verticalPadding?: number;
  font?: Font;
};

export function calculateOtherLabelsLayout(
  wrappedOtherLabels: string[][],
  config: OtherLabelsLayoutConfig = {}
): Rect[] {
  const {
    startX = 0,
    startY = 0,
    fixedWidth = null,
    labelSpacing = GROUP_PADDING,
    horizontalPadding = GROUP_PADDING,
    verticalPadding = VERTICAL_PADDING,
    font = MODERNIZED_BLOCK_DIAGRAM_OTHER_LABEL_FONT,
  } = config;

  const measureLabel = createMeasurerFromFont(font);

  const bounds: Rect[] = [];
  let currentY = startY;

  for (const wrappedLabel of wrappedOtherLabels) {
    const width =
      fixedWidth !== null
        ? fixedWidth
        : (wrappedLabel.length
            ? Math.max(...wrappedLabel.map(measureLabel))
            : 0) +
          2 * horizontalPadding;

    const height = wrappedLabel.length * LINE_HEIGHT + 2 * verticalPadding;

    bounds.push(new Rect(startX, currentY, width, height));
    currentY += height + labelSpacing;
  }

  return bounds;
}

export const LEAF_NODE_OTHER_LABELS_CONFIG: OtherLabelsLayoutConfig = {
  fixedWidth: MODERNIZED_BLOCK_DIAGRAM_LEAF_NODE_LABEL_WIDTH,
  labelSpacing: MODERNIZED_BLOCK_DIAGRAM_LEAF_NODE_PADDING,
  horizontalPadding: 0,
  verticalPadding: VERTICAL_PADDING,
  font: MODERNIZED_BLOCK_DIAGRAM_OTHER_LABEL_FONT,
};

export const GROUP_OTHER_LABELS_CONFIG: OtherLabelsLayoutConfig = {
  fixedWidth: null,
  labelSpacing: VERTICAL_MARGIN,
  horizontalPadding: PADDING,
  verticalPadding: VERTICAL_PADDING,
  font: MODERNIZED_BLOCK_DIAGRAM_OTHER_LABEL_FONT,
};

export const CANVAS_OTHER_LABELS_CONFIG: OtherLabelsLayoutConfig = {
  fixedWidth: null,
  labelSpacing: VERTICAL_MARGIN,
  horizontalPadding: PADDING,
  verticalPadding: VERTICAL_PADDING,
  font: MODERNIZED_BLOCK_DIAGRAM_OTHER_LABEL_FONT,
};

export const otherLabelsTotalHeight = (rects: Rect[]): number =>
  rects.length
    ? rects[rects.length - 1].y + rects[rects.length - 1].height - rects[0].y
    : 0;
